import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { MultiSelect } from '@/components/ui/multiselect';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { useRightPanel } from '@/contexts/RightPanelContext';
import { SaveFilterModal } from './SaveFilterModal';
import { SavedFiltersDropdown } from './SavedFiltersDropdown';
import { toast } from 'sonner';
import { savedFiltersService } from '@/services/saved-filters-service';
import { dataService, isDemoMode } from '@/services/data-service';
import type { PendingFilters, SavedFilter } from '@/contexts/RightPanelContext';
import type { Location } from '@/types/settings';

// Fallback mock data for dropdowns (used when API fails or in demo mode)
const STATUSES = ['Not Started', 'In Progress', 'On Hold', 'Pending Review'];
const PRIORITIES = ['Critical', 'High', 'Medium', 'Low'];
const AUDIT_TYPES = ['Full Audit', 'Spot Check', 'Verification', 'Reconciliation', 'Compliance'];
const FALLBACK_LOCATIONS = ['Head Office', 'Warehouse A', 'Warehouse B', 'Branch 1', 'Branch 2', 'Remote'];
const ASSIGNED_USERS = ['John Smith', 'Jane Doe', 'Bob Johnson', 'Alice Williams', 'Charlie Brown'];

export const ActiveAuditsFiltersTab: React.FC = () => {
  const { 
    setActiveFilterCount,
    pendingFilters,
    setPendingFilters,
    appliedFilters,
    applyFilters,
    clearAllFilters,
    savedFilters,
    setSavedFilters,
    loadSavedFilter,
  } = useRightPanel();

  // Status
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);

  // Priority
  const [selectedPriorities, setSelectedPriorities] = useState<string[]>([]);

  // Due Date Range
  const [dueDateFrom, setDueDateFrom] = useState<Date | undefined>();
  const [dueDateTo, setDueDateTo] = useState<Date | undefined>();

  // Audit Type
  const [selectedAuditTypes, setSelectedAuditTypes] = useState<string[]>([]);

  // Assigned Users
  const [selectedAssignedUsers, setSelectedAssignedUsers] = useState<string[]>([]);

  // Location
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);

  // Completion Percentage Range
  const [completionMin, setCompletionMin] = useState('');
  const [completionMax, setCompletionMax] = useState('');

  // Overdue Toggle
  const [overdueOnly, setOverdueOnly] = useState(false);

  // Save filter modal state
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [editingFilter, setEditingFilter] = useState<SavedFilter | null>(null);

  // Selected saved filter
  const [selectedSavedFilterId, setSelectedSavedFilterId] = useState<string | null>(null);

  // Loading state for API operations
  const [, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // API-fetched filter options
  const [apiLocations, setApiLocations] = useState<Location[]>([]);
  const [isLoadingFilterOptions, setIsLoadingFilterOptions] = useState(false);

  // Ref to track if we're loading from external source
  const isLoadingFromExternal = React.useRef(false);

  // Fetch filter options from API on mount
  useEffect(() => {
    const fetchFilterOptions = async () => {
      setIsLoadingFilterOptions(true);
      try {
        const locations = await dataService.getLocations();
        setApiLocations(locations);
      } catch (error) {
        console.error('Failed to fetch filter options:', error);
      } finally {
        setIsLoadingFilterOptions(false);
      }
    };
    fetchFilterOptions();
  }, []);

  // Compute filter options from API data (only use fallback in demo mode)
  const locationOptions = useMemo(() => {
    if (apiLocations.length > 0) {
      return apiLocations.map(loc => ({ label: loc.name, value: loc.name }));
    }
    if (isDemoMode()) {
      return FALLBACK_LOCATIONS.map(loc => ({ label: loc, value: loc }));
    }
    return [];
  }, [apiLocations]);

  // Load saved filters from API on component mount
  const loadSavedFiltersFromApi = useCallback(async () => {
    setIsLoading(true);
    try {
      const filters = await savedFiltersService.getSavedFilters();
      setSavedFilters(filters);
    } catch (error) {
      console.error('Failed to load saved filters:', error);
      toast.error('Failed to load saved filters');
    } finally {
      setIsLoading(false);
    }
  }, [setSavedFilters]);

  // Load saved filters on mount
  useEffect(() => {
    loadSavedFiltersFromApi();
  }, [loadSavedFiltersFromApi]);

  // Update pending filters whenever local state changes
  useEffect(() => {
    if (isLoadingFromExternal.current) {
      return;
    }
    
    const filters: PendingFilters = {
      selectedCategories: selectedAuditTypes,
      selectedStatuses,
      selectedLocations,
      acquisitionDateRange: { from: dueDateFrom, to: dueDateTo },
      acquisitionCostMin: completionMin,
      acquisitionCostMax: completionMax,
      netBookValueMin: '',
      netBookValueMax: '',
      selectedAssignedTo: selectedAssignedUsers,
      selectedDepreciationMethods: [],
      warrantyExpiryDateRange: { from: undefined, to: undefined },
      selectedManufacturers: selectedPriorities,
    };
    setPendingFilters(filters);
  }, [
    selectedStatuses,
    selectedPriorities,
    dueDateFrom,
    dueDateTo,
    selectedAuditTypes,
    selectedAssignedUsers,
    selectedLocations,
    completionMin,
    completionMax,
    overdueOnly,
    setPendingFilters,
  ]);

  // Load pending filters into local state when they change from external source
  useEffect(() => {
    if (pendingFilters) {
      isLoadingFromExternal.current = true;
      setSelectedAuditTypes(pendingFilters.selectedCategories);
      setSelectedStatuses(pendingFilters.selectedStatuses);
      setSelectedLocations(pendingFilters.selectedLocations);
      setDueDateFrom(pendingFilters.acquisitionDateRange.from);
      setDueDateTo(pendingFilters.acquisitionDateRange.to);
      setCompletionMin(pendingFilters.acquisitionCostMin);
      setCompletionMax(pendingFilters.acquisitionCostMax);
      setSelectedAssignedUsers(pendingFilters.selectedAssignedTo);
      setSelectedPriorities(pendingFilters.selectedManufacturers);
      setTimeout(() => {
        isLoadingFromExternal.current = false;
      }, 0);
    }
  }, [pendingFilters]);

  // Clear local state when filters are cleared
  useEffect(() => {
    if (!pendingFilters && !appliedFilters) {
      isLoadingFromExternal.current = true;
      setSelectedStatuses([]);
      setSelectedPriorities([]);
      setDueDateFrom(undefined);
      setDueDateTo(undefined);
      setSelectedAuditTypes([]);
      setSelectedAssignedUsers([]);
      setSelectedLocations([]);
      setCompletionMin('');
      setCompletionMax('');
      setOverdueOnly(false);
      setSelectedSavedFilterId(null);
      setTimeout(() => {
        isLoadingFromExternal.current = false;
      }, 0);
    }
  }, [pendingFilters, appliedFilters]);

  const handleClearAll = () => {
    isLoadingFromExternal.current = true;
    setSelectedStatuses([]);
    setSelectedPriorities([]);
    setDueDateFrom(undefined);
    setDueDateTo(undefined);
    setSelectedAuditTypes([]);
    setSelectedAssignedUsers([]);
    setSelectedLocations([]);
    setCompletionMin('');
    setCompletionMax('');
    setOverdueOnly(false);
    setSelectedSavedFilterId(null);
    clearAllFilters();
    setTimeout(() => {
      isLoadingFromExternal.current = false;
    }, 0);
  };

  const activeFilterCount = 
    selectedStatuses.length +
    selectedPriorities.length +
    (dueDateFrom || dueDateTo ? 1 : 0) +
    selectedAuditTypes.length +
    selectedAssignedUsers.length +
    selectedLocations.length +
    (completionMin || completionMax ? 1 : 0) +
    (overdueOnly ? 1 : 0);

  // Update the context with the active filter count
  useEffect(() => {
    setActiveFilterCount(activeFilterCount);
  }, [activeFilterCount, setActiveFilterCount]);

  const hasFilterChanges = () => {
    if (!appliedFilters && activeFilterCount === 0) return false;
    if (!appliedFilters && activeFilterCount > 0) return true;
    if (!pendingFilters) return false;
    return JSON.stringify(pendingFilters) !== JSON.stringify(appliedFilters);
  };

  const handleApplyFilters = () => {
    applyFilters();
  };

  const handleSaveAndApply = () => {
    if (activeFilterCount === 0) {
      toast.error('Please configure at least one filter before saving');
      return;
    }
    setSaveModalOpen(true);
  };

  const handleSaveFilter = async (name: string) => {
    if (!pendingFilters) return;

    setIsSaving(true);
    try {
      if (editingFilter) {
        const updatedFilter = await savedFiltersService.updateSavedFilter(
          editingFilter.id,
          name,
          pendingFilters
        );
        if (updatedFilter) {
          const updatedFilters = savedFilters.map((filter) =>
            filter.id === editingFilter.id ? updatedFilter : filter
          );
          setSavedFilters(updatedFilters);
          toast.success(`Filter '${name}' updated successfully`);
        } else {
          toast.error('Filter not found');
        }
        setEditingFilter(null);
      } else {
        const newFilter = await savedFiltersService.createSavedFilter(name, pendingFilters);
        setSavedFilters([...savedFilters, newFilter]);
        toast.success(`Filter '${name}' saved successfully`);
        setSelectedSavedFilterId(newFilter.id);
      }
      applyFilters();
    } catch (error) {
      console.error('Failed to save filter:', error);
      toast.error('Failed to save filter');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSelectSavedFilter = (filterId: string | null) => {
    setSelectedSavedFilterId(filterId);
    if (filterId) {
      loadSavedFilter(filterId);
    } else {
      handleClearAll();
    }
  };

  const handleEditFilter = (filter: SavedFilter) => {
    setEditingFilter(filter);
    setSaveModalOpen(true);
  };

  const handleDeleteFilter = async (filterId: string) => {
    try {
      const success = await savedFiltersService.deleteSavedFilter(filterId);
      if (success) {
        setSavedFilters(savedFilters.filter((f) => f.id !== filterId));
        if (selectedSavedFilterId === filterId) {
          setSelectedSavedFilterId(null);
        }
        toast.success('Filter deleted successfully');
      } else {
        toast.error('Filter not found');
      }
    } catch (error) {
      console.error('Failed to delete filter:', error);
      toast.error('Failed to delete filter');
    }
  };

  const handleDuplicateFilter = async (filter: SavedFilter) => {
    try {
      const duplicatedFilter = await savedFiltersService.createSavedFilter(
        `${filter.name} (Copy)`,
        filter.filters
      );
      setSavedFilters([...savedFilters, duplicatedFilter]);
      toast.success(`Filter '${duplicatedFilter.name}' created`);
      setSelectedSavedFilterId(duplicatedFilter.id);
      loadSavedFilter(duplicatedFilter.id);
    } catch (error) {
      console.error('Failed to duplicate filter:', error);
      toast.error('Failed to duplicate filter');
    }
  };

  const hasUnsavedChanges = hasFilterChanges();

  return (
    <div className="flex flex-col h-full">
      {/* Saved Filters Dropdown */}
      <div className="border-b bg-[#f8fafc] dark:bg-[#0f172b]" style={{ borderColor: '#eee' }}>
        <SavedFiltersDropdown
          savedFilters={savedFilters}
          selectedFilterId={selectedSavedFilterId}
          onSelectFilter={handleSelectSavedFilter}
          onEditFilter={handleEditFilter}
          onDeleteFilter={handleDeleteFilter}
          onDuplicateFilter={handleDuplicateFilter}
        />
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 pb-24">

      {/* Status Filter */}
      <div className="space-y-3">
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-semibold">Status</Label>
            {selectedStatuses.length > 0 && (
              <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                {selectedStatuses.length}
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground">Audit status to filter</p>
        </div>

        <MultiSelect
          options={STATUSES.map(status => ({ label: status, value: status }))}
          value={selectedStatuses}
          onValueChange={setSelectedStatuses}
          placeholder="Select statuses..."
        />
      </div>

      <Separator />

      {/* Priority Filter */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-semibold">Priority</Label>
          {selectedPriorities.length > 0 && (
            <Badge variant="secondary" className="h-5 px-1.5 text-xs">
              {selectedPriorities.length}
            </Badge>
          )}
        </div>

        <MultiSelect
          options={PRIORITIES.map(priority => ({ label: priority, value: priority }))}
          value={selectedPriorities}
          onValueChange={setSelectedPriorities}
          placeholder="Select priorities..."
        />
      </div>

      <Separator />

      {/* Due Date Range Filter */}
      <div className="space-y-3">
        <Label className="text-sm font-semibold">Due Date</Label>

        <DateRangePicker
          from={dueDateFrom}
          to={dueDateTo}
          onFromChange={setDueDateFrom}
          onToChange={setDueDateTo}
        />
      </div>

      <Separator />

      {/* Audit Type Filter */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-semibold">Audit Type</Label>
          {selectedAuditTypes.length > 0 && (
            <Badge variant="secondary" className="h-5 px-1.5 text-xs">
              {selectedAuditTypes.length}
            </Badge>
          )}
        </div>

        <MultiSelect
          options={AUDIT_TYPES.map(type => ({ label: type, value: type }))}
          value={selectedAuditTypes}
          onValueChange={setSelectedAuditTypes}
          placeholder="Select audit types..."
        />
      </div>

      <Separator />

      {/* Assigned Users Filter */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-semibold">Assigned Users</Label>
          {selectedAssignedUsers.length > 0 && (
            <Badge variant="secondary" className="h-5 px-1.5 text-xs">
              {selectedAssignedUsers.length}
            </Badge>
          )}
        </div>

        <MultiSelect
          options={ASSIGNED_USERS.map(user => ({ label: user, value: user }))}
          value={selectedAssignedUsers}
          onValueChange={setSelectedAssignedUsers}
          placeholder="Select users..."
        />
      </div>

      <Separator />

      {/* Location Filter */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-semibold">Location</Label>
          {selectedLocations.length > 0 && (
            <Badge variant="secondary" className="h-5 px-1.5 text-xs">
              {selectedLocations.length}
            </Badge>
          )}
        </div>

        <MultiSelect
          options={locationOptions}
          value={selectedLocations}
          onValueChange={setSelectedLocations}
          placeholder={isLoadingFilterOptions ? "Loading..." : "Select locations..."}
        />
      </div>

      <Separator />

      {/* Completion Percentage Range Filter */}
      <div className="space-y-3">
        <Label className="text-sm font-semibold">Completion Percentage</Label>

        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1.5">
            <Label htmlFor="completion-min" className="text-xs text-muted-foreground">Min (%)</Label>
            <Input
              id="completion-min"
              type="number"
              placeholder="0"
              min="0"
              max="100"
              value={completionMin}
              onChange={(e) => setCompletionMin(e.target.value)}
              className="h-9"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="completion-max" className="text-xs text-muted-foreground">Max (%)</Label>
            <Input
              id="completion-max"
              type="number"
              placeholder="100"
              min="0"
              max="100"
              value={completionMax}
              onChange={(e) => setCompletionMax(e.target.value)}
              className="h-9"
            />
          </div>
        </div>
      </div>

      <Separator />

      {/* Overdue Filter */}
      <div className="space-y-3">
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-semibold">Show Overdue Only</Label>
            <Button
              variant={overdueOnly ? "default" : "outline"}
              size="sm"
              onClick={() => setOverdueOnly(!overdueOnly)}
              className="h-8"
            >
              {overdueOnly ? 'On' : 'Off'}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">Filter to show only overdue audits</p>
        </div>
      </div>
      </div>

      {/* Fixed Footer */}
      <div className="sticky bottom-0 bg-background border-t shadow-lg p-4">
        {hasUnsavedChanges && (
          <div className="mb-3 text-center">
            <Badge variant="secondary" className="text-xs">
              Unsaved changes
            </Badge>
          </div>
        )}
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleSaveAndApply}
            disabled={activeFilterCount === 0 || isSaving}
            className="flex-1"
          >
            {isSaving ? 'Saving...' : 'Save and Apply'}
          </Button>
          <Button
            onClick={handleApplyFilters}
            disabled={activeFilterCount === 0}
            className="flex-1"
          >
            Apply Filter
          </Button>
        </div>
      </div>

      {/* Save Filter Modal */}
      {pendingFilters && (
        <SaveFilterModal
          open={saveModalOpen}
          onOpenChange={setSaveModalOpen}
          pendingFilters={pendingFilters}
          existingFilters={savedFilters}
          onSave={handleSaveFilter}
          editingFilter={editingFilter}
        />
      )}
    </div>
  );
};
